import { useMemo, useState } from 'react';

import { useSearch } from '@/hooks/useSearch';

import type { Option } from './TickerSearch';

export const useTickerSearch = (initialSearchQuery: string | null) => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');

  const { data, status } = useSearch(searchQuery);

  const listings = useMemo(() => {
    return data.reduce((acc, company) => {
      const primaryListing = {
        type: 'primary',
        id: company.objectID,
        label: company.name,
        name: company.name,
        website: company.website,
        uniqueSymbol: company.uniqueSymbol,
        tickerSymbol: company.tickerSymbol,
        exchangeCountryIso: company.exchangeCountryIso,
        tag: company.tag,
      };

      // Sort the secondary listings so that the exact unique symbol match comes first
      const secondaryListings =
        company.listings
          ?.map((listing) => ({
            type: 'secondary',
            id: String(listing.tradingItemId),
            label: listing.uniqueSymbol,
            name: company.name,
            exchangeCountryIso: listing.exchangeCountryIso,
            uniqueSymbol: listing.uniqueSymbol,
          }))
          .sort((a, b) => {
            const isExactMatchA = a.uniqueSymbol === searchQuery;
            const isExactMatchB = b.uniqueSymbol === searchQuery;
            if (isExactMatchA && !isExactMatchB) return -1;
            if (!isExactMatchA && isExactMatchB) return 1;
            return 0;
          }) ?? [];

      return [...acc, primaryListing, ...secondaryListings] as Option[];
    }, [] as Option[]);
  }, [data, searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    listings,
    status,
  };
};
